import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { DemoBadge, UniqueBadge } from "@/components/Logo";
import { formatPrice } from "@/lib/api";
import { t } from "@/lib/i18n";

export const PriceTag = ({ p, className = "" }) => (
  <div className={`flex items-baseline gap-2 ${className}`}>
    {p.sale_price ? (
      <>
        <span className="text-[#956842] font-semibold">{formatPrice(p.sale_price)}</span>
        <span className="text-[#242321]/45 line-through text-[0.85em]">{formatPrice(p.price)}</span>
      </>
    ) : (
      <span className="font-medium">{formatPrice(p.price)}</span>
    )}
  </div>
);

export const ProductCard = ({ p }) => {
  const { isFavorite, toggleFavorite, addToCart, setCartOpen } = useStore();
  const soldOut = p.stock === 0;
  const fav = isFavorite(p.slug);
  const quickAdd = () => {
    if (p.variants.length) return;
    addToCart(p);
    toast.success(`${p.name} aggiunto al carrello`);
    setCartOpen(true);
  };
  return (
    <div data-testid={`product-card-${p.slug}`} className="group">
      <div className="relative aspect-[3/4] overflow-hidden bg-[#E9DFCE]">
        <Link to={`/prodotto/${p.slug}`} data-testid={`product-card-link-${p.slug}`}>
          <img src={p.images[0]} alt={p.name} loading="lazy" className={`w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06] ${soldOut ? "grayscale-[60%] opacity-70" : ""}`} />
        </Link>
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          {p.demo && <DemoBadge />}
          {p.stock === 1 && <UniqueBadge testId={`product-card-unique-${p.slug}`} />}
          {soldOut && <span data-testid={`product-card-soldout-${p.slug}`} className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] bg-[#F5F0E8] text-[#39251D]">{t("soldOut")}</span>}
          {!soldOut && p.sale_price && <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] bg-[#956842] text-[#F5F0E8]">{t("sale")}</span>}
          {!soldOut && !p.sale_price && p.is_new && <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] bg-[#D5B98C] text-[#39251D]">{t("new")}</span>}
        </div>
        <button data-testid={`product-card-favorite-${p.slug}`} onClick={() => toggleFavorite(p)} aria-label={t("favorites")} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#F5F0E8]/85 backdrop-blur flex items-center justify-center hover:scale-110 transition-transform">
          <Heart className={`w-4 h-4 ${fav ? "fill-[#956842] text-[#956842]" : "text-[#39251D]"}`} strokeWidth={1.5} />
        </button>
        {!soldOut && !p.variants.length && (
          <button data-testid={`product-card-add-to-cart-${p.slug}`} onClick={quickAdd} className="absolute inset-x-3 bottom-3 py-3 bg-[#39251D]/90 text-[#F5F0E8] text-[11px] uppercase tracking-[0.2em] opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300 hidden md:block">
            {t("addToCart")}
          </button>
        )}
      </div>
      <Link to={`/prodotto/${p.slug}`} className="block pt-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#956842]">{p.category_name}</div>
        <h3 className="font-serif text-xl text-[#39251D] mt-1 leading-tight">{p.name}</h3>
        <PriceTag p={p} className="mt-1.5 text-sm" />
      </Link>
    </div>
  );
};
